import { NetworkStatus } from "@apollo/client";
import React, { useCallback, useMemo } from "react";
import { useWindowDimensions } from "react-native";

import groupNotesByDate from "@/features/notes/utils/groupNotesByDate";
import NotesItem from "@/features/notes/components/organisms/NotesItem";
import NotesListView from "./NotesListView";

import SkeletonList from "@/components/organisms/SkeletonList";
import SkeletonPlaceholder from "@/components/molecules/SkeletonPlaceholder";
import { Box } from "@/components/atoms/Box";
import { Heading } from "@/components/atoms/Heading";
import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";
import {
  NotesNoteFragment,
  useNotesListNotesByPlayerIdQuery,
} from "@/generated/graphql";
import useMobileScreen from "@/hooks/useMobileScreen";
import { SwipeableProvider } from "@/providers/Swipeable";
import { useTheme } from "@/providers/ThemeProvider";

/**
 *
 */
interface Props {
  playerId: string;
  useSimpleList?: boolean;
}

/**
 * Container component for notes list with pagination and refresh
 * @param root0 - Component props
 * @param root0.playerId - ID of the player whose notes are being displayed
 * @param root0.useSimpleList - Whether to use simple VStack list instead of SectionList
 */
const NotesListContainer = ({ playerId, useSimpleList = false }: Props) => {
  const { height } = useWindowDimensions();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { data, fetchMore, refetch, loading, networkStatus } =
    useNotesListNotesByPlayerIdQuery({
      variables: { playerId },
    });
  const nextToken = data?.listNotesByPlayerId.pageInfo.endCursor;
  const isSmallScreen = useMobileScreen();

  const handleEndReached = useCallback(() => {
    if (nextToken) {
      fetchMore({
        variables: {
          nextToken,
        },
      });
    }
  }, [fetchMore, nextToken]);

  const renderItem = useCallback(
    ({ item }: { item: NotesNoteFragment }) => <NotesItem note={item} />,
    []
  );

  const groupedNotes = useMemo(
    () =>
      groupNotesByDate(data?.listNotesByPlayerId.edges.map(e => e.node) ?? []),
    [data]
  );

  const handleRefresh = useCallback(
    () => refetch({ playerId }),
    [playerId, refetch]
  );

  const SkeletonHeader = useCallback(
    () => (
      <Box className="pb-4">
        <SkeletonPlaceholder>
          <Box className="h-7 w-1/2 bg-surface-action" />
        </SkeletonPlaceholder>
      </Box>
    ),
    []
  );

  const SkeletonSeparator = useCallback(() => <Box className="h-3" />, []);

  const renderListEmptyComponent = useCallback(
    () =>
      loading ? (
        <SkeletonList
          type="note"
          size={5}
          className="py-3"
          ListHeaderComponent={SkeletonHeader}
          ItemSeparatorComponent={SkeletonSeparator}
        />
      ) : (
        <Box className="flex-1 items-center justify-center">
          <Stack className="items-center justify-center p-3">
            <Heading
              variant="title"
              className={`${isSmallScreen ? "text-center" : "text-center text-title"} ${isDark ? "text-white" : "text-content-primary"}`}
            >
              No Notes Yet
            </Heading>
            <Text
              variant="unstyled"
              className={`${
                isSmallScreen
                  ? "text-center"
                  : "text-center font-medium text-body"
              } ${isDark ? "text-content-subtle" : "text-content-neutral-strong"}`}
            >
              Press the button above to leave a note
            </Text>
          </Stack>
        </Box>
      ),
    [loading, isSmallScreen, isDark, SkeletonHeader, SkeletonSeparator]
  );

  return (
    <SwipeableProvider>
      <NotesListView
        onEndReached={handleEndReached}
        renderItem={renderItem}
        refreshing={networkStatus === NetworkStatus.refetch}
        notes={groupedNotes}
        screenHeight={height}
        isSmallScreen={isSmallScreen}
        onRefresh={handleRefresh}
        renderListEmptyComponent={renderListEmptyComponent}
        isDark={isDark}
        useSimpleList={useSimpleList}
      />
    </SwipeableProvider>
  );
};

export default NotesListContainer;
